import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { formatCurrency } from '../lib/format';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';

export function ApplyCreditModal({ open, onClose, invoice, onDone }) {
  const [creditNotes, setCreditNotes] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [amount, setAmount] = useState('');
  const [notifyCustomer, setNotifyCustomer] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !invoice) return;
    setLoading(true);
    setSelectedId('');
    setAmount('');
    setNotifyCustomer(true);
    api
      .get(`/credit-notes?customer_id=${invoice.client_id}&available_only=true`)
      .then((r) => setCreditNotes(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCreditNotes([]))
      .finally(() => setLoading(false));
  }, [open, invoice]);

  const selected = creditNotes.find((cn) => cn.id === selectedId);
  const maxApply = selected
    ? Math.min(
        parseFloat(selected.amount_remaining) || 0,
        parseFloat(invoice?.balance_due) || 0
      )
    : 0;

  const handleApply = async () => {
    const amt = parseFloat(amount);
    if (!selectedId || !amt || amt <= 0) {
      toast.error('Select a credit note and enter a valid amount');
      return;
    }
    if (amt > maxApply + 0.01) {
      toast.error(`Amount cannot exceed ${formatCurrency(maxApply)}`);
      return;
    }
    setSaving(true);
    try {
      await api.post(`/credit-notes/${selectedId}/apply`, {
        invoice_id: invoice.id,
        amount: amt,
        notify_customer: notifyCustomer,
      });
      toast.success(notifyCustomer ? 'Credit applied and customer notified' : 'Credit applied to invoice');
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to apply credit');
    } finally {
      setSaving(false);
    }
  };

  if (!invoice) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Apply Credit Note — {invoice.invoice_number}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm bg-slate-50 p-3 rounded-md">
            <div>
              <span className="text-slate-500">Invoice Total</span>
              <p className="font-medium">{formatCurrency(invoice.total)}</p>
            </div>
            <div>
              <span className="text-slate-500">Payments Received</span>
              <p className="font-medium text-emerald-600">{formatCurrency(invoice.amount_paid)}</p>
            </div>
            <div>
              <span className="text-slate-500">Credits Applied</span>
              <p className="font-medium text-amber-600">{formatCurrency(invoice.credits_applied || 0)}</p>
            </div>
            <div>
              <span className="text-slate-500">Balance Due</span>
              <p className="font-medium">{formatCurrency(invoice.balance_due)}</p>
            </div>
          </div>

          {loading ? (
            <p className="text-sm text-slate-500">Loading available credits…</p>
          ) : creditNotes.length === 0 ? (
            <p className="text-sm text-slate-500">No available credit notes for this customer.</p>
          ) : (
            <>
              <div className="space-y-2">
                <Label>Credit Note</Label>
                <Select value={selectedId} onValueChange={setSelectedId}>
                  <SelectTrigger data-testid="apply-credit-select">
                    <SelectValue placeholder="Select credit note" />
                  </SelectTrigger>
                  <SelectContent>
                    {creditNotes.map((cn) => (
                      <SelectItem key={cn.id} value={cn.id}>
                        {cn.credit_note_number} — {formatCurrency(cn.amount_remaining)} available
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Amount to Apply (max {formatCurrency(maxApply)})</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  data-testid="apply-credit-amount"
                />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="notify-customer"
                  checked={notifyCustomer}
                  onCheckedChange={(v) => setNotifyCustomer(v === true)}
                  data-testid="apply-credit-notify"
                />
                <Label htmlFor="notify-customer" className="text-sm font-normal cursor-pointer">
                  Email customer the updated credit note (PDF + online view)
                </Label>
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            className="bg-blue-600 hover:bg-blue-700"
            onClick={handleApply}
            disabled={saving || !selectedId || creditNotes.length === 0}
            data-testid="apply-credit-submit"
          >
            {saving ? 'Applying…' : 'Apply Credit'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
